import React from 'react';
import {
  TitleAndSubtitle,
  ContentButtonsWrapper,
} from '../../layout/layout-container.styled';
import { Text, font } from '../../../style-utils/text';
import { StyledButton } from '../../styled-button/styled-button';
import { CheckRing } from '../../icons/check-ring';
import { BUILDING_ACTIVITIES } from '../../../constants/building-activities';
import { DailyLogState } from '../../hooks/use-daily-log-handler';
import { DefaultComponentProps } from '../../app-router/component-props';
import { toKebabCase } from '../../app-router/path-generator';
import { useOptionAdvance } from '../../hooks/use-option-advance';

/**
 * Building-activity picker — step 1 of the daily-log wizard. Renders ONLY
 * pane content: the persistent WizardLayout shell hosts it via <Outlet/>, so
 * Continue into the first step swaps just the pane (no shell remount).
 */
export const DailyLog: React.FC<DefaultComponentProps> = ({
  navigate,
  changeHandler,
  state,
  finish,
  allPaths,
}) => {
  const { buildingActivity } = state as DailyLogState;

  const allRoutes = allPaths || [];
  const { pendingValue, selectAndAdvance } = useOptionAdvance({
  /**
   * A NEW ENTRY STARTS WHEN A TYPE IS PICKED — NOT WHEN THIS SCREEN MOUNTS.
   *
   * This used to be `useEffect(() => { finish(); }, [])`, and `finish` replaces
   * the whole state with initialState. The picker is inside the persistent
   * wizard shell, so THREE one-tap routes reach this mount from inside a
   * part-filled entry — the shell's own "Visitor Log" rail item, a Back out of
   * step 1, and the sidebar. Any of them silently destroyed everything typed so
   * far: no confirmation, no undo, no sign that anything had happened.
   *
   * Clearing on SELECT is the same guarantee with none of the loss: picking a
   * type is the explicit act of starting a new entry, and it is the only way
   * into the steps. Arriving at the picker and leaving again now costs nothing.
   *
   * The two calls compose safely — both setters use functional updaters, so the
   * reset lands first and the type is written on top of it.
   */
    onSelect: value => {
      finish();
      changeHandler(value, 'buildingActivity', value);
    },
    onAdvance: value => {
      const basePath = `/${toKebabCase(value)}`;
      const firstMatchingPath = allRoutes.find(path =>
        path.startsWith(basePath)
      );

      navigate(
        firstMatchingPath ? `/daily-log${firstMatchingPath}` : '/main-menu',
        { replace: false }
      );
    },
  });

  return (
    <>
      {/* THE BACK BUTTON, TOP LEFT — the product's one design and one
          position (components/layout/back-button.tsx). It replaced a
          bottom-left <NavButton direction="back">, which was the wizard's
          STEP control being used for a PAGE exit: leaving the flow and
          stepping back inside it looked identical and sat in the same corner. */}
      {/* The BackBar that used to sit here is now rendered once by
          WizardLayout, inside StepPane (WP-10). Three routes each carried a
          copy and every step past the first had none, which is both the missing
          exit (BUG-011) and the ~60px title jump on entering a step
          (BUG-019). */}
      <TitleAndSubtitle direction="column">
        <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
          Building Activities
        </Text>
        <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
          Please select one of the options below
        </Text>
      </TitleAndSubtitle>
      <ContentButtonsWrapper>
        <StyledButton
          onClick={() => selectAndAdvance(BUILDING_ACTIVITIES.MAINTENANCE)}
          text={BUILDING_ACTIVITIES.MAINTENANCE}
          icon={<CheckRing />}
          isSelected={buildingActivity === BUILDING_ACTIVITIES.MAINTENANCE}
          pending={pendingValue === BUILDING_ACTIVITIES.MAINTENANCE}
        />
        <StyledButton
          onClick={() => selectAndAdvance(BUILDING_ACTIVITIES.LEASING_OFFICE)}
          text={BUILDING_ACTIVITIES.LEASING_OFFICE}
          icon={<CheckRing />}
          isSelected={buildingActivity === BUILDING_ACTIVITIES.LEASING_OFFICE}
          pending={pendingValue === BUILDING_ACTIVITIES.LEASING_OFFICE}
        />
        {/* NO 'RESIDENT' OPTION. Owner instruction, 2026-08-13: "remove
            resident routes from here, we have a fast entry for resident".

            A resident coming in or going out is a Resident Entry on the
            VISITOR log, recorded from the main menu's Resident pair in two
            taps against the building roster. This wizard's 'Resident' was a
            note ABOUT a resident on the daily log -- a different table and a
            different act, five steps long, and reliably the one the desk
            reached for by mistake because it is the word they were looking
            for.

            THE VOCABULARY VALUE STAYS. daily_log.buildingActivity still
            accepts 'Resident' server-side and vocabulary.js still teaches
            it, because rows already carry it and the reports, the charts
            and Lobbero IQ all have to keep reading them. What is removed is
            the way to write a NEW one. */}
        <StyledButton
          onClick={() => selectAndAdvance(BUILDING_ACTIVITIES.MISSCELANEAUS)}
          text={BUILDING_ACTIVITIES.MISSCELANEAUS}
          icon={<CheckRing />}
          isSelected={buildingActivity === BUILDING_ACTIVITIES.MISSCELANEAUS}
          pending={pendingValue === BUILDING_ACTIVITIES.MISSCELANEAUS}
        />
        <StyledButton
          onClick={() => selectAndAdvance(BUILDING_ACTIVITIES.SHIFT_CHANGE)}
          text={BUILDING_ACTIVITIES.SHIFT_CHANGE}
          icon={<CheckRing />}
          isSelected={buildingActivity === BUILDING_ACTIVITIES.SHIFT_CHANGE}
          pending={pendingValue === BUILDING_ACTIVITIES.SHIFT_CHANGE}
        />
      </ContentButtonsWrapper>
    </>
  );
};
